/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * AuthenticatedChatProvider - Internal SkillShop wrapper for ChatProvider
 * 
 * This component is ONLY for internal SkillShop apps that have AuthProvider
 * in their component tree. It automatically extracts the user from AuthContext.
 * 
 * External apps using SkillShop as a service should use ChatProvider directly
 * with the `user` prop, NOT this component. See docs/technical/SKILLSHOP_SERVICE.md.
 * 
 * This file is intentionally separate from the main ChatContext to avoid
 * bundling AuthContext into the agent-runtime bundle used by external apps.
 */

import React, { ReactNode } from 'react';
import { useAuth } from '../AuthContext';
import { ChatProvider, type ChatProviderProps } from './index';

/**
 * ChatProvider wrapper that automatically uses SkillShop's AuthContext for user info.
 * Use this in internal SkillShop apps that have AuthProvider in their component tree.
 * 
 * External apps should use ChatProvider directly with the `user` prop instead.
 */
export function AuthenticatedChatProvider({ 
  children, 
  contextProvider 
}: Omit<ChatProviderProps, 'user'>) {
  const { user } = useAuth();
  
  return (
    <ChatProvider user={user} contextProvider={contextProvider}>
      {children}
    </ChatProvider>
  );
}
